import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, Loader2, Inbox, AlertCircle, MessageSquare, Clock, CheckCircle2, ChevronRight, ChevronLeft, Paperclip, MailCheck, X, ArrowUpRight, Flame, Calendar, TrendingUp } from "lucide-react";
import { useToast } from "../../components/ToastProvider";

// Tickets from the public landing-page form have no `user` (guestName/guestEmail
// instead) since the submitter isn't a logged-in app user.
function ticketContact(t) {
  const email = t.user?.email || t.guestEmail || "";
  const label = t.guestName ? `${t.guestName} (${email || "no email"})` : email || "—";
  const initial = (t.guestName || email || "?")[0]?.toUpperCase() || "?";
  return { label, initial };
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber: "#111111", amberBg: "rgba(0,0,0,0.06)", amberBorder: "rgba(0,0,0,0.12)",
  dark: "#0c1118", bg: "#f4f6f8", surface: "#ffffff", border: "#e8ecf0",
  muted: "#6b7280", mutedLight: "#9ca3af",
  green: "#16a34a", greenBg: "rgba(34,197,94,0.1)", greenBorder: "rgba(34,197,94,0.25)",
  red: "#dc2626", redBg: "rgba(239,68,68,0.1)", redBorder: "rgba(239,68,68,0.25)",
};

if (!document.getElementById("arp-styles")) {
  const s = document.createElement("style");
  s.id = "arp-styles";
  s.textContent = `
    @keyframes arp-spin { to { transform: rotate(360deg); } }
    .arp-spin { animation: arp-spin 0.7s linear infinite; }
    .arp-row:hover td { background: rgba(0,0,0,0.03); }
    .arp-btn:hover { opacity: 0.82; }
    .arp-search:focus { outline: none; border-color: #111111; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
    .arp-select:focus { outline: none; border-color: #111111; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
    .arp-textarea:focus { outline: none; border-color: #111111; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
  `;
  document.head.appendChild(s);
}

const STATUS_META = {
  open:    { label: "Open",    bg: "rgba(239,68,68,0.1)",   color: "#dc2626", border: "rgba(239,68,68,0.25)",    Icon: Clock },
  pending: { label: "Pending", bg: "rgba(0,0,0,0.06)",       color: "#333333", border: "rgba(0,0,0,0.12)",       Icon: MessageSquare },
  closed:  { label: "Closed",  bg: "rgba(34,197,94,0.1)",   color: "#16a34a", border: "rgba(34,197,94,0.25)",    Icon: CheckCircle2 },
};

const API = process.env.REACT_APP_BACKEND_URL;

async function fetchJson(url, opts) {
  const r = await fetch(url, { credentials: "include", ...opts });
  if (!r.ok) throw new Error(await r.text().catch(() => r.statusText));
  return r.json();
}

function TicketMobileCard({ t, onView }) {
  const sm = STATUS_META[t.status] || STATUS_META.open;
  return (
    <div
      style={{
        background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10,
        marginBottom: 10, overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ padding: "12px 14px" }}>
        {/* Subject + status */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
          <div style={{ fontWeight: 700, color: T.dark, fontSize: 14, lineHeight: 1.3 }}>{t.subject}</div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, background: sm.bg, color: sm.color, border: `1px solid ${sm.border}`, whiteSpace: "nowrap", flexShrink: 0 }}>
            <sm.Icon size={11} />{sm.label}
          </span>
        </div>
        {/* User + date */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontBody, fontSize: 11, fontWeight: 700, color: T.amber }}>
              {ticketContact(t).initial}
            </div>
            <span style={{ fontSize: 12, color: T.dark, fontWeight: 600 }}>{ticketContact(t).label}</span>
          </div>
          <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.mutedLight }}>
            <Calendar size={11} />{new Date(t.createdAt).toLocaleDateString()}
          </span>
          {t.priority > 0 && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, background: T.redBg, color: T.red, border: `1px solid ${T.redBorder}` }}>
              <Flame size={10} /> High
            </span>
          )}
        </div>
        {/* Message preview */}
        {t.message && (
          <div style={{ fontSize: 12, color: T.mutedLight, marginTop: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {t.message}
          </div>
        )}
      </div>
      <div style={{ padding: "8px 14px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", justifyContent: "flex-end" }}>
        <button className="arp-btn" onClick={() => onView(t.id)}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 16px", background: T.dark, color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          View <ArrowUpRight size={13} />
        </button>
      </div>
    </div>
  );
}

export default function SupportDesk() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [counts, setCounts] = useState({ all: 0, open: 0, pending: 0, closed: 0 });
  const [drawerId, setDrawerId] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [updating, setUpdating] = useState(false);
  const toast = useToast();
  const searchRef = useRef(null);
  const statusParam = tab === "all" ? "" : tab;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  async function loadUnread() {
    try { const d = await fetchJson(`${API}/api/admin/support/unread-count`); setUnread(Number(d.unread || 0)); } catch {}
  }

  async function loadList() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (statusParam) params.set("status", statusParam);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      const d = await fetchJson(`${API}/api/admin/support/tickets?` + params.toString());
      setRows(d.items || []);
      setTotal(Number(d.total || 0));
      Promise.all([
        fetchJson(`${API}/api/admin/support/tickets?page=1&pageSize=1`),
        fetchJson(`${API}/api/admin/support/tickets?status=open&page=1&pageSize=1`),
        fetchJson(`${API}/api/admin/support/tickets?status=pending&page=1&pageSize=1`),
        fetchJson(`${API}/api/admin/support/tickets?status=closed&page=1&pageSize=1`),
      ]).then(([a, o, p, c]) => setCounts({ all: a.total || 0, open: o.total || 0, pending: p.total || 0, closed: c.total || 0 })).catch(() => {});
    } finally { setLoading(false); }
  }

  async function loadTicket(id) {
    setDrawerLoading(true);
    try { const d = await fetchJson(`${API}/api/admin/support/tickets/${id}`); setTicket(d.ticket || null); loadUnread(); loadList(); }
    catch { setTicket(null); }
    finally { setDrawerLoading(false); }
  }

  useEffect(() => { loadUnread(); }, []); // eslint-disable-line
  useEffect(() => { loadList(); }, [tab, page, pageSize]); // eslint-disable-line
  useEffect(() => {
    const el = searchRef.current;
    function onKey(e) { if (e.key === "Enter") { setPage(1); loadList(); } }
    el?.addEventListener("keydown", onKey);
    return () => el?.removeEventListener("keydown", onKey);
  }, [q, tab]); // eslint-disable-line

  async function changeStatus(nextStatus) {
    if (!ticket) return;
    setUpdating(true);
    const prev = ticket;
    setTicket((t) => t ? { ...t, status: nextStatus } : t);
    try { await fetchJson(`${API}/api/admin/support/tickets/${ticket.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: nextStatus }) }); loadList(); }
    catch { setTicket(prev); toast.error("Failed to update status."); }
    finally { setUpdating(false); }
  }

  async function changePriority(nextPriority) {
    if (!ticket) return;
    setUpdating(true);
    const prev = ticket;
    setTicket((t) => t ? { ...t, priority: nextPriority } : t);
    try { await fetchJson(`${API}/api/admin/support/tickets/${ticket.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ priority: Number(nextPriority) }) }); loadList(); }
    catch { setTicket(prev); toast.error("Failed to update priority."); }
    finally { setUpdating(false); }
  }

  async function sendReply() {
    const body = reply.trim();
    if (!body || !ticket) return;
    setUpdating(true);
    try { await fetchJson(`${API}/api/admin/support/tickets/${ticket.id}/reply`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }) }); setReply(""); await loadTicket(ticket.id); }
    catch { toast.error("Failed to send reply."); }
    finally { setUpdating(false); }
  }

  function openDrawer(id) { setDrawerId(id); setTicket(null); loadTicket(id); }
  function closeDrawer() { setDrawerId(null); setTicket(null); setReply(""); }

  const TABS = ["all", "open", "pending", "closed"];

  return (
    <>
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.fontBody }}>
      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>Support Desk</h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>Manage incoming tickets and provide customer support</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {[{ icon: Inbox, label: "Unread", value: unread }, { icon: TrendingUp, label: "Open", value: counts.open }].map(({ icon: Icon, label, value }) => (
            <div key={label} style={{ background: "#ffffff", border: "1px solid #e8ecf0", borderRadius: 8, padding: "10px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 12, marginBottom: 4 }}>
                <Icon size={14} />{label}
              </div>
              <div style={{ fontFamily: T.fontBody, fontSize: 20, fontWeight: 700, color: "#0c1118" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: isMobile ? "16px" : "0" }}>
        {/* Tabs + Search */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: isMobile ? "12px 14px" : "16px 20px", marginBottom: 20 }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: 12 }}>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 6, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {TABS.map((k) => {
                const active = tab === k;
                return (
                  <button key={k} onClick={() => { setTab(k); setPage(1); }} style={{ padding: isMobile ? "7px 12px" : "8px 16px", borderRadius: 7, border: `1px solid ${active ? T.dark : T.border}`, background: active ? T.dark : T.surface, color: active ? "#fff" : T.muted, fontWeight: active ? 700 : 400, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", flexShrink: 0 }}>
                    <span style={{ textTransform: "capitalize" }}>{k}</span>
                    <span style={{ padding: "1px 7px", borderRadius: 99, fontSize: 11, fontFamily: T.fontBody, background: active ? "rgba(255,255,255,0.15)" : T.bg, color: active ? "#fff" : T.muted }}>{k === "all" ? counts.all : counts[k] || 0}</span>
                  </button>
                );
              })}
            </div>
            {/* Search */}
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ position: "relative", flex: 1 }}>
                <Search size={16} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.mutedLight }} />
                <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tickets..." className="arp-search"
                  style={{ paddingLeft: 34, paddingRight: 12, paddingTop: 9, paddingBottom: 9, border: `1px solid ${T.border}`, borderRadius: 7, fontSize: 13, fontFamily: T.fontBody, width: isMobile ? "100%" : 260, background: T.bg, boxSizing: "border-box" }} />
              </div>
              <button className="arp-btn" onClick={() => { setPage(1); loadList(); }}
                style={{ padding: "9px 16px", background: T.dark, color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>Search</button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 60, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: T.muted }}>
            <Loader2 size={28} className="arp-spin" />
            <p style={{ margin: 0, fontSize: 14 }}>Loading tickets…</p>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 60, textAlign: "center" }}>
            <AlertCircle size={32} color={T.border} style={{ margin: "0 auto 12px" }} />
            <p style={{ fontWeight: 700, color: T.dark, margin: "0 0 4px" }}>No tickets found</p>
            <p style={{ color: T.muted, fontSize: 13, margin: 0 }}>Try adjusting your search or filters</p>
          </div>
        ) : isMobile ? (
          <>
            {rows.map((t) => (
              <TicketMobileCard key={t.id} t={t} onView={openDrawer} />
            ))}
            {/* Mobile pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 4 }}>
              <button className="arp-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: "8px 16px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.surface, cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.4 : 1, fontSize: 13 }}>
                Previous
              </button>
              <span style={{ fontSize: 12, color: T.muted }}>{page} / {totalPages}</span>
              <button className="arp-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ padding: "8px 16px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.surface, cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1, fontSize: 13 }}>
                Next
              </button>
            </div>
          </>
        ) : (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                    {["Created", "User", "Subject", "Status", "Priority", "Actions"].map((h, i) => (
                      <th key={h} style={{ padding: "12px 20px", textAlign: i === 5 ? "right" : "left", fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: T.muted }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => {
                    const sm = STATUS_META[t.status] || STATUS_META.open;
                    return (
                      <tr key={t.id} className="arp-row" style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: "14px 20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, color: T.muted, fontSize: 12 }}>
                            <Calendar size={13} color={T.mutedLight} />
                            {new Date(t.createdAt).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: 11, color: T.mutedLight }}>{new Date(t.createdAt).toLocaleTimeString()}</div>
                        </td>
                        <td style={{ padding: "14px 20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 30, height: 30, borderRadius: "50%", background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontBody, fontSize: 12, fontWeight: 700, color: T.amber }}>
                              {ticketContact(t).initial}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: T.dark, fontSize: 13 }}>{ticketContact(t).label}</div>
                              <div style={{ fontSize: 11, color: T.mutedLight }}>{t.orgId ? "Company" : "Individual"}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "14px 20px" }}>
                          <div style={{ fontWeight: 600, color: T.dark, marginBottom: 2 }}>{t.subject}</div>
                          <div style={{ fontSize: 11, color: T.mutedLight, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{t.message}</div>
                        </td>
                        <td style={{ padding: "14px 20px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, background: sm.bg, color: sm.color, border: `1px solid ${sm.border}` }}>
                            <sm.Icon size={11} />{sm.label}
                          </span>
                        </td>
                        <td style={{ padding: "14px 20px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, background: t.priority > 0 ? T.redBg : T.bg, color: t.priority > 0 ? T.red : T.muted, border: `1px solid ${t.priority > 0 ? T.redBorder : T.border}` }}>
                            <Flame size={11} />{t.priority > 0 ? `High (${t.priority})` : "Normal"}
                          </span>
                        </td>
                        <td style={{ padding: "14px 20px", textAlign: "right" }}>
                          <button className="arp-btn" onClick={() => openDrawer(t.id)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 14px", background: T.dark, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            View <ArrowUpRight size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderTop: `1px solid ${T.border}`, background: T.bg }}>
              <p style={{ fontSize: 13, color: T.muted, margin: 0 }}>
                {rows.length ? <><b style={{ color: T.dark }}>{(page - 1) * pageSize + 1}</b>–<b style={{ color: T.dark }}>{Math.min(page * pageSize, total)}</b> of <b style={{ color: T.dark }}>{total}</b></> : "No results"}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button className="arp-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ padding: 7, border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface, cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.4 : 1 }}>
                  <ChevronLeft size={16} />
                </button>
                <span style={{ padding: "6px 14px", border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface, fontSize: 13, fontWeight: 600 }}>{page} / {totalPages}</span>
                <button className="arp-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  style={{ padding: 7, border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface, cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? 0.4 : 1 }}>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Ticket Drawer — rendered via portal to avoid stacking-context issues */}
    {drawerId && createPortal(
      <>
        <div onClick={closeDrawer} style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.4)" }} />
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9999, width: "100%", maxWidth: isMobile ? "100%" : 580, background: T.surface, boxShadow: "-8px 0 40px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>
          {/* Drawer header */}
          <div style={{ background: T.dark, padding: "20px 24px", color: "#fff" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.5)", fontSize: 12, marginBottom: 6 }}>
                  <Calendar size={13} />{ticket?.createdAt ? new Date(ticket.createdAt).toLocaleString() : "—"}
                </div>
                <h2 style={{ fontFamily: T.fontBody, fontSize: 20, color: "#fff", margin: 0, letterSpacing: "0.03em" }}>{ticket?.subject || "Ticket Details"}</h2>
              </div>
              <button onClick={closeDrawer} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 7, padding: 7, cursor: "pointer", color: "#fff", marginLeft: 12 }}>
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Drawer body */}
          {drawerLoading ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: T.muted }}>
              <Loader2 size={28} className="arp-spin" /><p style={{ margin: 0 }}>Loading ticket details…</p>
            </div>
          ) : !ticket ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: T.muted }}>
              <AlertCircle size={28} /><p style={{ margin: 0 }}>Ticket not found.</p>
            </div>
          ) : (
            <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
              {/* Meta */}
              <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: "50%", background: T.amberBg, border: `1px solid ${T.amberBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: T.fontBody, fontSize: 16, fontWeight: 700, color: T.amber }}>
                    {ticketContact(ticket).initial}
                  </div>
                  <div>
                    <p style={{ fontSize: 11, color: T.mutedLight, margin: 0 }}>From</p>
                    <p style={{ fontWeight: 600, color: T.dark, margin: 0 }}>{ticketContact(ticket).label}</p>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                  <select disabled={updating} value={ticket.status} onChange={(e) => changeStatus(e.target.value)} className="arp-select"
                    style={{ flex: 1, padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 7, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark }}>
                    <option value="open">Open</option>
                    <option value="pending">Pending</option>
                    <option value="closed">Closed</option>
                  </select>
                  <select disabled={updating} value={ticket.priority ?? 0} onChange={(e) => changePriority(Number(e.target.value))} className="arp-select"
                    style={{ flex: 1, padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 7, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark }}>
                    <option value={0}>Normal</option>
                    <option value={1}>High (1)</option>
                    <option value={2}>High (2)</option>
                  </select>
                </div>

                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px", fontSize: 13, color: T.dark, whiteSpace: "pre-wrap", marginBottom: 12 }}>
                  {ticket.message || "—"}
                </div>

                {ticket.attachments?.length ? (
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 700, color: T.muted, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 5 }}><Paperclip size={14} />Attachments</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {ticket.attachments.map((a) => (
                        <a key={a.id} href={`${API}/api/brand/logo/${a.fileId}`} target="_blank" rel="noreferrer"
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, color: T.dark, textDecoration: "none" }}>
                          <Paperclip size={12} /> View File
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Thread */}
              <div style={{ flex: 1, padding: "20px 24px", background: T.bg }}>
                <p style={{ fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: T.muted, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 5 }}>
                  <MessageSquare size={12} /> Conversation
                </p>
                {ticket.messages?.length ? ticket.messages.map((m) => (
                  <div key={m.id} style={{ borderRadius: 8, border: `1px solid ${m.author === "admin" ? T.amberBorder : T.border}`, padding: "12px 14px", fontSize: 13, background: m.author === "admin" ? T.amberBg : T.surface, marginLeft: m.author === "admin" ? 24 : 0, marginRight: m.author === "admin" ? 0 : 24, marginBottom: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: T.dark, fontSize: 12 }}>{m.author === "admin" ? "Support Team" : "User"}</span>
                      <span style={{ fontSize: 11, color: T.mutedLight }}>{new Date(m.createdAt).toLocaleString()}</span>
                    </div>
                    <p style={{ margin: 0, color: T.dark, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{m.body}</p>
                  </div>
                )) : (
                  <div style={{ textAlign: "center", paddingTop: 32, color: T.muted }}>
                    <MessageSquare size={32} color={T.border} style={{ margin: "0 auto 8px" }} />
                    <p style={{ margin: 0, fontSize: 13 }}>No replies yet.</p>
                  </div>
                )}
              </div>

              {/* Reply composer */}
              <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, background: T.surface }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: T.muted, margin: "0 0 8px", display: "flex", alignItems: "center", gap: 5 }}><MailCheck size={13} /> Reply to Customer</p>
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} className="arp-textarea"
                  style={{ width: "100%", border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, fontSize: 13, fontFamily: T.fontBody, resize: "none", background: T.bg, color: T.dark, boxSizing: "border-box" }}
                  placeholder="Write a helpful and professional reply…" />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                  <span style={{ fontSize: 11, color: T.mutedLight, display: "flex", alignItems: "center", gap: 4 }}><MailCheck size={12} /> Reply will be sent via email</span>
                  <button className="arp-btn" onClick={sendReply} disabled={updating || reply.trim().length < 2}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 20px", background: T.dark, color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: updating || reply.trim().length < 2 ? "not-allowed" : "pointer", opacity: updating || reply.trim().length < 2 ? 0.5 : 1 }}>
                    {updating ? <><Loader2 size={15} className="arp-spin" /> Sending…</> : <><MailCheck size={15} /> Send Reply</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </>,
      document.body
    )}
    </>
  );
}
